import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { CodeBracketIcon } from "@heroicons/react/24/outline";
import {
  buildFlowFromQuestions,
  getQuestionNodes,
  normalizeFlow,
} from "./guideFlowCore";
import { parseImportedGuideFlow } from "./guideFlowImportUtils";
import {
  autoLayoutGuideFlow,
  flowNodesVisuallyCrowded,
  shouldCompactClusterLayout,
} from "./guideFlowAutoLayout";
import { GuideFlowJsonDialog } from "./GuideFlowJsonDialog";
import { AIGenerator } from "../AIGenerator";
import { aiGeneratorConfigs } from "../../../config/aiGeneratorConfigs";
import "../../dashboard/offers/calculations/module-builder.css";

export const GUIDE_FLOW_DRAG_MIME = "application/promang-guide-flow-node";

export function getEffectDefinition(effectNodeDefinitions, effectType) {
  return (effectNodeDefinitions || []).find((item) => item.type === effectType) || null;
}

/** Resolved labels for tying palette blocks ↔ canvas nodes ↔ editor (same `effect_type` id). */
export function getEffectFlowLabels(effectNodeDefinitions, effectType, t) {
  const def = getEffectDefinition(effectNodeDefinitions, effectType);
  const title = def?.titleKey ? t(def.titleKey) : "";
  return { type: effectType || "", title };
}

/** Human-readable destination for edges / option rows (question vs effect node). */
export function getGuideFlowTargetSummary(flow, effectNodeDefinitions, targetNodeId, t) {
  const node = (flow?.nodes || []).find((n) => n.id === targetNodeId);
  if (!node) {
    return { kind: "missing", primary: targetNodeId || "", code: "", nodeTitle: "" };
  }
  if (node.type === "effect") {
    const { title, type } = getEffectFlowLabels(effectNodeDefinitions, node.data?.effect_type, t);
    const paletteName = title || type || t("Effekt");
    const nodeTitle = String(node.data?.label || node.title || "").trim();
    const redundant =
      !nodeTitle ||
      nodeTitle === paletteName ||
      nodeTitle === type ||
      nodeTitle === title;
    return {
      kind: "effect",
      primary: paletteName,
      code: type || "",
      nodeTitle: redundant ? "" : nodeTitle,
    };
  }
  return {
    kind: "question",
    primary: String(node.data?.label || node.title || node.id || "").trim(),
    code: "",
    nodeTitle: "",
  };
}

function truncateGuideFlowLabel(text, maxLen) {
  const s = text == null ? "" : String(text);
  if (s.length <= maxLen) return s;
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`;
}

const ANSWER_TYPES = ["single_select", "multi_select", "boolean", "text", "textarea", "date"];
const CANVAS_SIZE = 3600;
export const DEFAULT_GUIDE_FLOW_VIEWPORT = { x: 80, y: 40, zoom: 1 };

/**
 * @param {object} props
 * @param {object} props.template — template with settings + questions (fallback when flow missing)
 * @param {function} props.onChange — (nextFlow) => void
 * @param {Array<{type: string, titleKey: string, defaultData: object}>} props.effectNodeDefinitions
 * @param {React.ComponentType<{effects: object, onChange: function, checklistTypes?: array, effectType?: string|null}>} props.EffectsEditorComponent
 * @param {array} [props.checklistTypes]
 * @param {object} [props.defaultQuestionData] merged into new question nodes (e.g. include_in_report)
 * @param {function} [props.renderQuestionExtras] ({ data, onUpdateData, t }) => ReactNode
 * @param {string} [props.markerIdPrefix]
 * @param {boolean} [props.effectsOnQuestionOptions] — when false, answer options only show label/value (effects via separate effect nodes)
 * @param {React.ReactNode} [props.questionOptionsCaption] — optional hint under answer options heading
 * @param {string} [props.guideFlowKind] — `case_onboarding` | `field_report` for AI / authoring context
 */
export function GuideFlowBuilder({
  template,
  onChange,
  effectNodeDefinitions,
  EffectsEditorComponent,
  checklistTypes = [],
  defaultQuestionData = {},
  renderQuestionExtras,
  markerIdPrefix = "guide-flow",
  effectsOnQuestionOptions = true,
  questionOptionsCaption = null,
  guideFlowKind = "",
}) {
  const { t } = useTranslation();
  const workspaceRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  /** Run crowded-flow fix at most once per template id so later drags are not overwritten by auto-layout. */
  const autoLayoutOncePerTemplateRef = useRef(new Map());
  const initialFlow = useMemo(
    () => normalizeFlow(template?.settings?.flow || buildFlowFromQuestions(template?.questions || []), template?.questions || []),
    [template?.id, template?.settings?.flow, template?.questions]
  );
  const [selectedNodeId, setSelectedNodeId] = useState(initialFlow.start_node_id || initialFlow.nodes?.[0]?.id || null);
  const [connectDraft, setConnectDraft] = useState(null);
  const [edgeContextMenu, setEdgeContextMenu] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [panState, setPanState] = useState(null);
  const [flowJsonOpen, setFlowJsonOpen] = useState(false);

  const flow = normalizeFlow(template?.settings?.flow || initialFlow, template?.questions || []);
  const viewport = flow.viewport || DEFAULT_GUIDE_FLOW_VIEWPORT;
  const questionNodes = getQuestionNodes(flow);
  const selectedNode = flow.nodes.find((node) => node.id === selectedNodeId);
  const flowEdges = buildEdgesFromOptions(flow);

  /** Saved templates often keep bad x/y from older imports — compact once per template when crowded (not on every flow edit). */
  useLayoutEffect(() => {
    const tid = String(template?.id ?? "__new__");
    const f = normalizeFlow(
      template?.settings?.flow || buildFlowFromQuestions(template?.questions || []),
      template?.questions || []
    );

    if (!flowNodesVisuallyCrowded(f) && !shouldCompactClusterLayout(f)) {
      return;
    }

    if (autoLayoutOncePerTemplateRef.current.get(tid)) {
      return;
    }

    autoLayoutOncePerTemplateRef.current.set(tid, true);
    const fixed = autoLayoutGuideFlow(f);
    onChangeRef.current({
      ...fixed,
      viewport: fixed.viewport || DEFAULT_GUIDE_FLOW_VIEWPORT,
      edges: buildEdgesFromOptions(fixed),
      version: 1,
    });
  }, [template?.id, template?.settings?.flow, template?.questions]);

  useEffect(() => {
    if (!edgeContextMenu) return undefined;

    const closeMenu = (event) => {
      if (event?.target?.closest?.(".offer-module-builder__context-menu")) return;
      setEdgeContextMenu(null);
    };
    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("contextmenu", closeMenu);

    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("contextmenu", closeMenu);
    };
  }, [edgeContextMenu]);

  useEffect(() => {
    const nodeExists = flow.nodes.some((node) => node.id === selectedNodeId);
    if (!nodeExists) {
      setSelectedNodeId(flow.start_node_id || flow.nodes[0]?.id || null);
    }
  }, [flow.start_node_id, flow.nodes, selectedNodeId]);

  const updateFlow = (updater) => {
    const next = typeof updater === "function" ? updater(flow) : updater;
    onChange({
      ...next,
      viewport: next.viewport || flow.viewport || DEFAULT_GUIDE_FLOW_VIEWPORT,
      edges: buildEdgesFromOptions(next),
      version: 1,
    });
  };

  const applyNormalizedGuideFlow = (normalized) => {
    onChange({
      ...normalized,
      viewport: normalized.viewport || flow.viewport || DEFAULT_GUIDE_FLOW_VIEWPORT,
      edges: buildEdgesFromOptions(normalized),
      version: 1,
    });
  };

  const handleAIGeneratedFlow = (generatedFields) => {
    const raw = generatedFields?.flow_json;
    if (raw == null || String(raw).trim() === "") {
      toast.error(t("guide.flow.ai_empty"));
      return;
    }
    try {
      const normalized = parseImportedGuideFlow(raw, template?.questions || []);
      applyNormalizedGuideFlow(normalized);
    } catch (e) {
      toast.error(t("guide.flow.json_invalid", { message: e?.message || String(e) }));
    }
  };

  const updateViewport = (updater) => {
    updateFlow((current) => {
      const currentViewport = current.viewport || DEFAULT_GUIDE_FLOW_VIEWPORT;
      const nextViewport = typeof updater === "function" ? updater(currentViewport) : updater;
      return {
        ...current,
        viewport: {
          x: nextViewport.x,
          y: nextViewport.y,
          zoom: clamp(nextViewport.zoom, 0.35, 2),
        },
      };
    });
  };

  const screenToWorld = (clientX, clientY) => {
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const currentViewport = flow.viewport || DEFAULT_GUIDE_FLOW_VIEWPORT;
    return {
      x: (clientX - rect.left - currentViewport.x) / currentViewport.zoom,
      y: (clientY - rect.top - currentViewport.y) / currentViewport.zoom,
    };
  };

  const zoomCanvas = (factor) => {
    updateViewport((current) => ({ ...current, zoom: current.zoom * factor }));
  };

  const zoomCanvasAtPoint = (factor, clientX, clientY) => {
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) {
      zoomCanvas(factor);
      return;
    }

    const before = screenToWorld(clientX, clientY);
    updateViewport((current) => {
      const nextZoom = clamp(current.zoom * factor, 0.35, 2);
      const canvasX = clientX - rect.left;
      const canvasY = clientY - rect.top;
      return {
        zoom: nextZoom,
        x: canvasX - before.x * nextZoom,
        y: canvasY - before.y * nextZoom,
      };
    });
  };

  const updateNode = (nodeId, patch) => {
    updateFlow((current) => ({
      ...current,
      nodes: current.nodes.map((node) => (node.id === nodeId ? { ...node, ...patch } : node)),
    }));
  };

  const updateNodeData = (nodeId, patch) => {
    updateFlow((current) => ({
      ...current,
      nodes: current.nodes.map((node) =>
        node.id === nodeId ? { ...node, data: { ...(node.data || {}), ...patch } } : node
      ),
    }));
  };

  const addQuestionNode = () => {
    const suffix = Date.now();
    const node = {
      id: `question_${suffix}`,
      type: "question",
      title: t("Nytt spørsmål"),
      x: 120 + questionNodes.length * 260,
      y: 140,
      data: {
        key: `question_${questionNodes.length + 1}`,
        label: t("Nytt spørsmål"),
        help_text: "",
        answer_type: "single_select",
        required: false,
        options: [{ label: t("Valg 1"), value: "option_1", effects: {} }],
        effects: [],
        ...defaultQuestionData,
      },
    };
    updateFlow((current) => ({
      ...current,
      start_node_id: current.start_node_id || node.id,
      nodes: [...current.nodes, node],
    }));
    setSelectedNodeId(node.id);
  };

  const addEffectNode = (effectType, x, y) => {
    const definition =
      (effectNodeDefinitions || []).find((item) => item.type === effectType) ||
      effectNodeDefinitions?.[0] || {
        type: effectType,
        titleKey: "Effekt",
        defaultData: { effects: {} },
      };
    const suffix = Date.now();
    const node = {
      id: `effect_${effectType}_${suffix}`,
      type: "effect",
      title: t(definition.titleKey),
      x: x ?? 160 + flow.nodes.length * 240,
      y: y ?? 320,
      data: {
        effect_type: effectType,
        label: t(definition.titleKey),
        next_node_id: "",
        ...(definition.defaultData || {}),
      },
    };
    updateFlow((current) => ({
      ...current,
      nodes: [...current.nodes, node],
    }));
    setSelectedNodeId(node.id);
  };

  const deleteNode = (nodeId) => {
    updateFlow((current) => {
      const remainingNodes = current.nodes.filter((node) => node.id !== nodeId);
      const cleanedNodes = remainingNodes.map((node) => ({
        ...node,
        data: {
          ...(node.data || {}),
          options: (node.data?.options || []).map((option) =>
            option.next_node_id === nodeId ? { ...option, next_node_id: "" } : option
          ),
          next_node_id: node.data?.next_node_id === nodeId ? "" : node.data?.next_node_id,
        },
      }));
      return {
        ...current,
        nodes: cleanedNodes,
        start_node_id: current.start_node_id === nodeId ? cleanedNodes[0]?.id || null : current.start_node_id,
      };
    });
    setSelectedNodeId(null);
  };

  const updateOption = (nodeId, optionIndex, patch) => {
    const node = flow.nodes.find((item) => item.id === nodeId);
    const options = node?.data?.options || [];
    updateNodeData(nodeId, {
      options: options.map((option, index) => (index === optionIndex ? { ...option, ...patch } : option)),
    });
  };

  const addOption = (nodeId) => {
    const node = flow.nodes.find((item) => item.id === nodeId);
    const options = node?.data?.options || [];
    updateNodeData(nodeId, {
      options: [
        ...options,
        {
          label: `${t("Valg")} ${options.length + 1}`,
          value: `option_${options.length + 1}`,
          effects: {},
        },
      ],
    });
  };

  const removeOption = (nodeId, optionIndex) => {
    const node = flow.nodes.find((item) => item.id === nodeId);
    const options = node?.data?.options || [];
    updateNodeData(nodeId, {
      options: options.filter((_, index) => index !== optionIndex),
    });
  };

  const deleteEdge = (edge) => {
    if (!edge?.source) return;
    if (edge.kind === "effect") {
      updateNodeData(edge.source, { next_node_id: "" });
      setEdgeContextMenu(null);
      return;
    }
    if (edge.optionIndex === undefined) return;
    updateFlow((current) => {
      const nodes = current.nodes.map((node) => {
        if (node.id !== edge.source) return node;

        const options = node.data?.options || [];
        return {
          ...node,
          data: {
            ...(node.data || {}),
            options: options.map((option, index) => {
              const isTargetOption =
                index === edge.optionIndex ||
                (String(option.value) === String(edge.value) && String(option.next_node_id) === String(edge.target));
              return isTargetOption ? { ...option, next_node_id: "" } : option;
            }),
          },
        };
      });

      return {
        ...current,
        nodes,
        edges: buildEdgesFromOptions({ ...current, nodes }),
      };
    });
    setEdgeContextMenu(null);
  };

  const connectOptionToNode = (targetNodeId) => {
    if (!connectDraft || connectDraft.sourceNodeId === targetNodeId) {
      setSelectedNodeId(targetNodeId);
      return;
    }

    if (connectDraft.kind === "effect") {
      updateNodeData(connectDraft.sourceNodeId, { next_node_id: targetNodeId });
    } else {
      updateOption(connectDraft.sourceNodeId, connectDraft.optionIndex, { next_node_id: targetNodeId });
    }
    setConnectDraft(null);
    setSelectedNodeId(targetNodeId);
  };

  const onPointerMove = (event) => {
    if (dragState) {
      const currentViewport = flow.viewport || DEFAULT_GUIDE_FLOW_VIEWPORT;
      const dx = (event.clientX - dragState.clientX) / currentViewport.zoom;
      const dy = (event.clientY - dragState.clientY) / currentViewport.zoom;
      updateNode(dragState.nodeId, {
        x: Math.max(0, dragState.x + dx),
        y: Math.max(60, dragState.y + dy),
      });
      return;
    }

    if (panState) {
      updateViewport({
        ...viewport,
        x: panState.x + event.clientX - panState.clientX,
        y: panState.y + event.clientY - panState.clientY,
      });
    }
  };

  const startPan = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest(".offer-module-builder__node")) return;
    if (event.target.closest(".offer-module-builder__context-menu")) return;
    setSelectedNodeId(null);
    setEdgeContextMenu(null);
    setPanState({
      clientX: event.clientX,
      clientY: event.clientY,
      x: viewport.x,
      y: viewport.y,
    });
  };

  const stopDrag = () => {
    setDragState(null);
    setPanState(null);
  };

  const onDrop = (event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData(GUIDE_FLOW_DRAG_MIME);
    if (!type) return;
    const point = screenToWorld(event.clientX, event.clientY);
    if (type.startsWith("effect:")) {
      addEffectNode(type.replace("effect:", ""), point.x - 110, point.y - 40);
      return;
    }
    if (type !== "question") return;
    const node = {
      id: `question_${Date.now()}`,
      type: "question",
      title: t("Nytt spørsmål"),
      x: point.x - 110,
      y: point.y - 40,
      data: {
        key: `question_${questionNodes.length + 1}`,
        label: t("Nytt spørsmål"),
        help_text: "",
        answer_type: "single_select",
        required: false,
        options: [{ label: t("Valg 1"), value: "option_1", effects: {} }],
        effects: [],
        ...defaultQuestionData,
      },
    };
    updateFlow((current) => ({
      ...current,
      start_node_id: current.start_node_id || node.id,
      nodes: [...current.nodes, node],
    }));
    setSelectedNodeId(node.id);
  };

  const arrowMarkerId = `${markerIdPrefix}-arrow`;

  return (
    <div className="offer-module-builder">
      <GuideFlowJsonDialog
        fallbackQuestions={template?.questions || []}
        flow={flow}
        onApplyNormalizedFlow={applyNormalizedGuideFlow}
        onClose={() => setFlowJsonOpen(false)}
        open={flowJsonOpen}
      />
      <aside className="offer-module-builder__panel">
        <h3 className="offer-module-builder__section-title">{t("Guide builder")}</h3>
        <p className="offer-module-builder__hint">
          {t("Opprett spørsmål som noder og koble svar til neste spørsmål.")}
        </p>
        <button
          className="offer-module-builder__palette-item"
          draggable
          type="button"
          onClick={addQuestionNode}
          onDragStart={(event) => event.dataTransfer.setData(GUIDE_FLOW_DRAG_MIME, "question")}
        >
          <span>
            <span className="block">{t("Spørsmål")}</span>
            <span className="offer-module-builder__hint">{t("Dra inn eller klikk for å opprette.")}</span>
          </span>
          <span>+</span>
        </button>
        {(effectNodeDefinitions || []).length > 0 ? (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <h4 className="offer-module-builder__section-title">{t("Effekter")}</h4>
            {(effectNodeDefinitions || []).map((definition) => (
              <button
                className="offer-module-builder__palette-item"
                draggable
                key={definition.type}
                type="button"
                onClick={() => addEffectNode(definition.type)}
                onDragStart={(event) =>
                  event.dataTransfer.setData(GUIDE_FLOW_DRAG_MIME, `effect:${definition.type}`)
                }
              >
                <span className="offer-module-builder__palette-item-body">
                  <span className="block">{t(definition.titleKey)}</span>
                  <span className="offer-module-builder__hint">{t("Guide flow palette effect id line", { type: definition.type })}</span>
                </span>
                <span className="offer-module-builder__palette-effect-code" title={definition.type}>
                  {definition.type}
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </aside>

      <section className="offer-module-builder__canvas">
        <div className="offer-module-builder__canvas-toolbar">
          <div>
            <h3 className="offer-module-builder__section-title">{t("Guide flow")}</h3>
            <p className="offer-module-builder__hint">
              {connectDraft
                ? t("Velg neste spørsmål for valgt svar.")
                : t("Koble svar til neste spørsmål ved å bruke Koble-knappen på et svar.")}
            </p>
          </div>
          <div className="offer-module-builder__toolbar-actions flex-wrap gap-2">
            <button
              className="offer-module-builder__button inline-flex items-center gap-1"
              type="button"
              title={t("guide.flow.open_json_tooltip")}
              onClick={() => setFlowJsonOpen(true)}
            >
              <CodeBracketIcon className="h-4 w-4 shrink-0" aria-hidden />
              {t("guide.flow.open_json")}
            </button>
            <AIGenerator
              buttonLabel={t("guide.flow.generate_with_ai")}
              className="!inline-flex !items-center !gap-1 !border-sky-200 !bg-sky-50 !px-2 !py-1 !text-xs !font-medium !text-sky-900 hover:!bg-sky-100"
              config={aiGeneratorConfigs.guide_flow}
              context={{
                guide_description: template?.description || "",
                guide_kind: guideFlowKind || "",
                guide_title: template?.title || "",
              }}
              fieldsToGenerate={["flow_json"]}
              module="guide_flow"
              onGenerate={handleAIGeneratedFlow}
            />
            <button className="offer-module-builder__button" type="button" onClick={() => zoomCanvas(0.85)}>
              -
            </button>
            <span className="offer-module-builder__zoom-label">{Math.round(viewport.zoom * 100)}%</span>
            <button className="offer-module-builder__button" type="button" onClick={() => zoomCanvas(1.15)}>
              +
            </button>
          </div>
        </div>

        <div
          className="offer-module-builder__workspace"
          onDragOver={(event) => event.preventDefault()}
          onDrop={onDrop}
          onWheel={(event) => {
            event.preventDefault();
            zoomCanvasAtPoint(event.deltaY > 0 ? 0.92 : 1.08, event.clientX, event.clientY);
          }}
          onPointerMove={onPointerMove}
          onPointerDown={startPan}
          onPointerUp={stopDrag}
          onPointerLeave={stopDrag}
          ref={workspaceRef}
        >
          <div
            className="offer-module-builder__world"
            style={{
              height: CANVAS_SIZE,
              transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
              width: CANVAS_SIZE,
            }}
          >
            <svg className="offer-module-builder__edges" height={CANVAS_SIZE} width={CANVAS_SIZE}>
              <defs>
                <marker id={arrowMarkerId} markerHeight="10" markerWidth="10" orient="auto" refX="9" refY="5">
                  <path d="M0,0 L10,5 L0,10 z" fill="#0284c7" />
                </marker>
              </defs>
              {flowEdges.map((edge) => {
                const source = flow.nodes.find((node) => node.id === edge.source);
                const target = flow.nodes.find((node) => node.id === edge.target);
                if (!source || !target) return null;
                const path = edgePath(source, target);
                const anchor = edgeLabelAnchor(source, target);
                const targetSummary = getGuideFlowTargetSummary(flow, effectNodeDefinitions, edge.target, t);
                const edgeLabelText =
                  edge.kind === "effect"
                    ? ""
                    : truncateGuideFlowLabel(
                        `${edge.label || ""} → ${targetSummary.primary}${targetSummary.code ? ` (${targetSummary.code})` : ""}`,
                        48
                      );
                return (
                  <g key={edge.id} className="offer-module-builder__edge">
                    <path
                      d={path}
                      fill="none"
                      markerEnd={`url(#${arrowMarkerId})`}
                      stroke="#bae6fd"
                      strokeWidth="2"
                    />
                    {edgeLabelText ? (
                      <text
                        className="offer-module-builder__edge-label"
                        dominantBaseline="middle"
                        pointerEvents="none"
                        textAnchor="middle"
                        x={anchor.x}
                        y={anchor.y - 6}
                      >
                        {edgeLabelText}
                      </text>
                    ) : null}
                    <path
                      className="offer-module-builder__edge-hit"
                      d={path}
                      fill="none"
                      stroke="transparent"
                      strokeWidth="30"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        /* Menu uses position:fixed — viewport coords */
                        setEdgeContextMenu({
                          edge,
                          clientX: event.clientX,
                          clientY: event.clientY,
                        });
                      }}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setEdgeContextMenu({
                          edge,
                          clientX: event.clientX,
                          clientY: event.clientY,
                        });
                      }}
                    />
                  </g>
                );
              })}
            </svg>

            {flow.nodes.map((node) => {
              const effectLabels =
                node.type === "effect"
                  ? getEffectFlowLabels(effectNodeDefinitions, node.data?.effect_type, t)
                  : null;
              return (
              <div
                className={[
                  "offer-module-builder__node",
                  selectedNodeId === node.id ? "is-selected" : "",
                  connectDraft?.sourceNodeId === node.id ? "is-connecting" : "",
                  node.type === "effect" ? "is-effect" : "",
                ].join(" ")}
                key={node.id}
                style={{ left: node.x, top: node.y }}
                onClick={(event) => {
                  event.stopPropagation();
                  connectOptionToNode(node.id);
                }}
              >
                <div
                  className="offer-module-builder__node-header"
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setDragState({ nodeId: node.id, clientX: event.clientX, clientY: event.clientY, x: node.x, y: node.y });
                    setSelectedNodeId(node.id);
                  }}
                >
                  <div>
                    <div className="offer-module-builder__node-title">{node.data?.label || node.title}</div>
                    <div className="offer-module-builder__node-type">
                      {node.type === "effect" ? (
                        <span className="offer-module-builder__node-type-effect">
                          <span className="offer-module-builder__node-type-effect-label">{t("Effekt")}</span>
                          {effectLabels?.title ? (
                            <span className="offer-module-builder__node-type-effect-name">{effectLabels.title}</span>
                          ) : null}
                          {effectLabels?.type ? (
                            <code className="offer-module-builder__node-effect-type-code">{effectLabels.type}</code>
                          ) : null}
                        </span>
                      ) : flow.start_node_id === node.id ? (
                        t("Start")
                      ) : (
                        t("Spørsmål")
                      )}
                    </div>
                  </div>
                  <button
                    className="offer-module-builder__node-action"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedNodeId(node.id);
                    }}
                  >
                    {t("Rediger")}
                  </button>
                </div>
                <div className="space-y-1">
                  {node.type === "effect" ? (
                    <div className="flex items-center justify-between gap-2 rounded-lg bg-sky-50 px-2 py-1 text-xs">
                      <span className="truncate">
                        {node.data?.next_node_id
                          ? `${t("Videre")}: ${flow.nodes.find((candidate) => candidate.id === node.data.next_node_id)?.data?.label || node.data.next_node_id}`
                          : t("Avslutt etter denne effekten")}
                      </span>
                      <button
                        type="button"
                        className="font-semibold text-sky-700"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedNodeId(node.id);
                          setConnectDraft({ kind: "effect", sourceNodeId: node.id });
                        }}
                      >
                        {node.data?.next_node_id ? t("Koble om") : t("Koble")}
                      </button>
                    </div>
                  ) : null}
                  {(node.data?.options || []).map((option, optionIndex) => {
                    const targetSummary = option.next_node_id
                      ? getGuideFlowTargetSummary(flow, effectNodeDefinitions, option.next_node_id, t)
                      : null;
                    return (
                    <div key={`${option.value}-${optionIndex}`} className="offer-module-builder__question-option">
                      <div className="offer-module-builder__question-option-main">
                        <span className="offer-module-builder__question-option-label truncate">{option.label || option.value}</span>
                        <button
                          type="button"
                          className="font-semibold text-sky-700 offer-module-builder__question-option-link-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedNodeId(node.id);
                            setConnectDraft({ sourceNodeId: node.id, optionIndex });
                          }}
                        >
                          {option.next_node_id ? t("Koble om") : t("Koble")}
                        </button>
                      </div>
                      {option.next_node_id && targetSummary ? (
                        <div className="offer-module-builder__question-option-target" title={option.next_node_id}>
                          <span className="offer-module-builder__question-option-target-arrow" aria-hidden>
                            →
                          </span>
                          <span className="offer-module-builder__question-option-target-primary">{targetSummary.primary}</span>
                          {targetSummary.code ? (
                            <code className="offer-module-builder__question-option-target-code">{targetSummary.code}</code>
                          ) : null}
                          {targetSummary.nodeTitle ? (
                            <span className="offer-module-builder__question-option-target-custom-title">
                              «{truncateGuideFlowLabel(targetSummary.nodeTitle, 28)}»
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        </div>
        {edgeContextMenu ? (
          <div
            className="offer-module-builder__context-menu"
            style={{ left: edgeContextMenu.clientX, top: edgeContextMenu.clientY }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                deleteEdge(edgeContextMenu.edge);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              {t("Slett kobling")}
            </button>
          </div>
        ) : null}
      </section>

      <aside className="offer-module-builder__result">
        {selectedNode ? (
          <GuideFlowNodeEditor
            EffectsEditorComponent={EffectsEditorComponent}
            checklistTypes={checklistTypes}
            effectNodeDefinitions={effectNodeDefinitions}
            effectsOnQuestionOptions={effectsOnQuestionOptions}
            flow={flow}
            node={selectedNode}
            questionOptionsCaption={questionOptionsCaption}
            renderQuestionExtras={renderQuestionExtras}
            onAddOption={() => addOption(selectedNode.id)}
            onDelete={() => deleteNode(selectedNode.id)}
            onRemoveOption={(optionIndex) => removeOption(selectedNode.id, optionIndex)}
            onSetStart={() => updateFlow((current) => ({ ...current, start_node_id: selectedNode.id }))}
            onUpdateData={(patch) => updateNodeData(selectedNode.id, patch)}
            onUpdateNode={(patch) => updateNode(selectedNode.id, patch)}
            onUpdateOption={(optionIndex, patch) => updateOption(selectedNode.id, optionIndex, patch)}
            t={t}
          />
        ) : (
          <div className="text-sm text-slate-500">{t("Velg en node for å redigere.")}</div>
        )}
      </aside>
    </div>
  );
}

function coerceEffectEditorPayload(effects) {
  if (effects && typeof effects === "object" && !Array.isArray(effects)) return effects;
  return {};
}

function GuideFlowNodeEditor({
  checklistTypes,
  EffectsEditorComponent,
  effectNodeDefinitions,
  effectsOnQuestionOptions,
  flow,
  node,
  questionOptionsCaption,
  renderQuestionExtras,
  onAddOption,
  onDelete,
  onRemoveOption,
  onSetStart,
  onUpdateData,
  onUpdateNode,
  onUpdateOption,
  t,
}) {
  const data = node.data || {};
  const options = data.options || [];

  if (node.type === "effect") {
    const effectLabels = getEffectFlowLabels(effectNodeDefinitions, data.effect_type, t);
    return (
      <div>
        <h3 className="offer-module-builder__section-title">{t("Rediger effekt")}</h3>
        {data.effect_type ? (
          <div className="offer-module-builder__effect-palette-link">
            <div className="offer-module-builder__effect-palette-link-title">{t("Guide flow effect editor palette link")}</div>
            <div className="offer-module-builder__effect-palette-link-body">
              {effectLabels.title ? (
                <span className="offer-module-builder__effect-palette-link-name">{effectLabels.title}</span>
              ) : null}
              <code className="offer-module-builder__effect-palette-link-code">{data.effect_type}</code>
            </div>
          </div>
        ) : null}
        <GuideFlowField label={t("Tittel")} value={data.label || ""} onChange={(value) => onUpdateData({ label: value })} />
        <EffectsEditorComponent
          checklistTypes={checklistTypes}
          effectType={data.effect_type || null}
          effects={coerceEffectEditorPayload(data.effects)}
          onChange={(effects) => onUpdateData({ effects })}
        />
        <div className="mt-4 text-xs text-slate-500">
          {data.next_node_id
            ? `${t("Videre")}: ${flow.nodes.find((candidate) => candidate.id === data.next_node_id)?.data?.label || data.next_node_id}`
            : t("Avslutt etter denne effekten")}
        </div>
        <button className="offer-module-builder__button is-danger mt-4" type="button" onClick={onDelete}>
          {t("Slett")}
        </button>
      </div>
    );
  }

  return (
    <div>
      <h3 className="offer-module-builder__section-title">{t("Rediger spørsmål")}</h3>
      <GuideFlowField label={t("Spørsmålstekst")} value={data.label || ""} onChange={(value) => onUpdateData({ label: value })} />
      <GuideFlowField
        label="key"
        value={data.key || ""}
        onChange={(value) => onUpdateData({ key: value.replace(/\s+/g, "_").toLowerCase() })}
      />
      <GuideFlowField
        label={t("Type")}
        value={data.answer_type || "single_select"}
        options={ANSWER_TYPES.map((type) => ({ value: type, label: type }))}
        onChange={(value) => onUpdateData({ answer_type: value })}
      />
      <GuideFlowField label={t("Hjelpetekst")} type="textarea" value={data.help_text || ""} onChange={(value) => onUpdateData({ help_text: value })} />
      <label className="mt-3 flex items-center gap-2 text-xs text-slate-700">
        <input type="checkbox" checked={data.required === true} onChange={(event) => onUpdateData({ required: event.target.checked })} />
        {t("Påkrevd")}
      </label>
      {typeof renderQuestionExtras === "function" ? renderQuestionExtras({ data, onUpdateData, t }) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <button className="offer-module-builder__button" type="button" onClick={onSetStart}>
          {t("Sett som start")}
        </button>
        <button className="offer-module-builder__button is-danger" type="button" onClick={onDelete}>
          {t("Slett")}
        </button>
      </div>

      {["single_select", "multi_select"].includes(data.answer_type) ? (
        <div className="mt-4 space-y-3">
          <h4 className="offer-module-builder__section-title">{t("Svarvalg")}</h4>
          {questionOptionsCaption ? (
            <p className="text-xs leading-relaxed text-slate-600">{questionOptionsCaption}</p>
          ) : null}
          {options.map((option, optionIndex) => {
            const targetSummary = option.next_node_id
              ? getGuideFlowTargetSummary(flow, effectNodeDefinitions, option.next_node_id, t)
              : null;
            return (
            <div key={optionIndex} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <GuideFlowField label={t("Valg label")} value={option.label || ""} onChange={(value) => onUpdateOption(optionIndex, { label: value })} />
              <GuideFlowField label={t("Verdi")} value={option.value || ""} onChange={(value) => onUpdateOption(optionIndex, { value })} />
              {option.next_node_id && targetSummary ? (
                <div className="offer-module-builder__editor-option-link mt-2 rounded-lg border border-teal-100 bg-teal-50/90 px-2.5 py-2 text-xs text-teal-900">
                  <div className="font-semibold text-teal-950">{t("Guide flow option connection")}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium">{option.label || option.value}</span>
                    <span className="text-teal-700" aria-hidden>
                      →
                    </span>
                    <span>{targetSummary.primary}</span>
                    {targetSummary.code ? (
                      <code className="offer-module-builder__editor-option-link-code">{targetSummary.code}</code>
                    ) : null}
                    {targetSummary.nodeTitle ? (
                      <span className="text-teal-800">«{targetSummary.nodeTitle}»</span>
                    ) : null}
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-teal-700/90" title={option.next_node_id}>
                    id: {option.next_node_id}
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-xs text-slate-500">{t("Avslutt etter dette svaret")}</div>
              )}
              {effectsOnQuestionOptions && EffectsEditorComponent ? (
                <EffectsEditorComponent
                  checklistTypes={checklistTypes}
                  effectType={null}
                  effects={coerceEffectEditorPayload(option.effects)}
                  onChange={(effects) => onUpdateOption(optionIndex, { effects })}
                />
              ) : null}
              <button className="offer-module-builder__button is-danger mt-2" type="button" onClick={() => onRemoveOption(optionIndex)}>
                {t("Fjern valg")}
              </button>
            </div>
            );
          })}
          <button className="offer-module-builder__button" type="button" onClick={onAddOption}>
            + {t("Legg til valg")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function GuideFlowField({ label, value, type = "text", options, onChange }) {
  return (
    <div className="offer-module-builder__field">
      <label>{label}</label>
      <div className="offer-module-builder__field-row">
        {options ? (
          <select value={value || ""} onChange={(event) => onChange(event.target.value)}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === "textarea" ? (
          <textarea value={value || ""} onChange={(event) => onChange(event.target.value)} />
        ) : (
          <input type={type} value={value ?? ""} onChange={(event) => onChange(event.target.value)} />
        )}
      </div>
    </div>
  );
}

export function buildEdgesFromOptions(flow) {
  return (flow.nodes || []).flatMap((node) => {
    const optionEdges = (node.data?.options || [])
      .filter((option) => node.type === "question" && option.next_node_id)
      .map((option, index) => ({
        id: `edge-${node.id}-${option.value || index}-${option.next_node_id}`,
        source: node.id,
        target: option.next_node_id,
        optionIndex: index,
        value: option.value,
        label: option.label || option.value,
        when: { value: option.value },
      }));

    const effectEdge =
      node.type === "effect" && node.data?.next_node_id
        ? [
            {
              id: `edge-${node.id}-effect-${node.data.next_node_id}`,
              source: node.id,
              target: node.data.next_node_id,
              kind: "effect",
              label: node.data?.label || node.title || "Effect",
              when: { always: true },
            },
          ]
        : [];

    return [...optionEdges, ...effectEdge];
  });
}

export function getEdgeGeometry(source, target) {
  const startX = Number(source.x || 0) + 230;
  const startY = Number(source.y || 0) + 70;
  const endX = Number(target.x || 0);
  const endY = Number(target.y || 0) + 70;
  const midX = startX + Math.max(40, (endX - startX) / 2);
  return { startX, startY, endX, endY, midX };
}

/** Center-ish point for option-edge labels (world coordinates). */
export function edgeLabelAnchor(source, target) {
  const { midX, startY, endY } = getEdgeGeometry(source, target);
  return { x: midX, y: (startY + endY) / 2 };
}

export function edgePath(source, target) {
  const { startX, startY, endX, endY, midX } = getEdgeGeometry(source, target);
  return `M ${startX} ${startY} L ${midX} ${startY} L ${midX} ${endY} L ${endX} ${endY}`;
}

export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
